import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Menu,
  Paper,
  Divider
} from '@mui/material';
import {
  TrendingDown,
  Add,
  Edit,
  Delete,
  FileDownload,
  Search,
  FilterList,
  ArrowBack,
  MoreVert,
  Refresh,
  Warning,
  ErrorOutline,
  CheckCircle
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth } from 'date-fns';

// Import services and components
import inventoryService from '../services/inventoryService';
import menuService from '../../../services/menuService';
import WastageForm from './components/WastageForm';

const MotionDiv = motion.div;

const WastageLog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const _isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // States
  const [wastageRecords, setWastageRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form states
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(startOfMonth(new Date()));
  const [dateTo, setDateTo] = useState(endOfMonth(new Date()));
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Menu and dialog states
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Wastage reasons - consistent with WastageForm
  const wastageReasons = [
    { value: 'expired', label: t('inventory.wastageReasons.expired') || 'Expired', color: 'error' },
    { value: 'damaged', label: t('inventory.wastageReasons.damaged') || 'Damaged', color: 'warning' },
    { value: 'spoiled', label: t('inventory.wastageReasons.spoiled') || 'Spoiled', color: 'error' },
    { value: 'returned', label: t('inventory.wastageReasons.returned') || 'Customer Return', color: 'info' },
    { value: 'overproduction', label: t('inventory.wastageReasons.overproduction') || 'Overproduction', color: 'warning' },
    { value: 'contaminated', label: t('inventory.wastageReasons.contaminated') || 'Contaminated', color: 'error' },
    { value: 'other', label: t('inventory.wastageReasons.other') || 'Other', color: 'default' }
  ];

  // Role-based permissions helper
  const getUserRole = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.role?.toLowerCase() || 'staff';
      } catch (error) {
        console.error('Error parsing user data:', error);
        return 'staff';
      }
    }
    return 'staff';
  };

  const userRole = getUserRole();
  const canEdit = ['admin', 'manager'].includes(userRole);
  const canDelete = userRole === 'admin';
  const canExport = ['admin', 'manager'].includes(userRole);

  // Fetch wastage records
  const fetchWastageRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 WastageLog: Fetching wastage records...');
      
      // Mock API call - replace with actual service
      const response = await inventoryService.getWastageRecords({
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd')
      });
      
      if (response && response.success) {
        setWastageRecords(response.data || []);
        console.log('✅ WastageLog: Records loaded:', response.data?.length || 0);
      } else {
        // Mock data for demonstration
        setWastageRecords([
          {
            wastage_id: 1,
            date: '2024-01-15',
            product_id: 1,
            product_name: 'Ice Kacang',
            quantity_wasted: 5,
            reason: 'expired',
            notes: 'Ingredients expired overnight',
            created_by: 'John Doe',
            created_at: '2024-01-15T10:30:00Z'
          },
          {
            wastage_id: 2,
            date: '2024-01-16',
            product_id: 2,
            product_name: 'Mee Goreng',
            quantity_wasted: 3,
            reason: 'damaged',
            notes: 'Spilled during preparation',
            created_by: 'Jane Smith',
            created_at: '2024-01-16T14:20:00Z'
          }
        ]);
      }
    } catch (err) {
      console.error('❌ WastageLog: Error fetching records:', err);
      setError(err.message || 'Failed to load wastage records');
      setWastageRecords([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  // Fetch products for dropdown
  const fetchProducts = useCallback(async () => {
    try {
      console.log('📦 WastageLog: Fetching products...');
      
      const response = await menuService.getAllWithCategories();
      setProducts(response || []);
      console.log('✅ WastageLog: Products loaded:', response?.length || 0);
    } catch (err) {
      console.error('❌ WastageLog: Error fetching products:', err);
      setProducts([]);
    }
  }, []);

  // Load data on mount and filter changes
  useEffect(() => {
    fetchWastageRecords();
    fetchProducts();
  }, [fetchWastageRecords, fetchProducts]);

  // Filtered records
  const filteredRecords = wastageRecords.filter(record => {
    const matchesSearch = record.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = productFilter === 'all' || record.product_id === parseInt(productFilter);
    const matchesReason = reasonFilter === 'all' || record.reason === reasonFilter;
    
    return matchesSearch && matchesProduct && matchesReason;
  });

  // Handle form operations
  const handleAddRecord = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setFormOpen(true);
    setActionAnchorEl(null);
  };

  const handleSaveRecord = async (recordData) => {
    try {
      setFormLoading(true);
      console.log('💾 WastageLog: Saving record:', recordData);
      
      if (editingRecord) {
        // Update existing record
        await inventoryService.updateWastageRecord(editingRecord.wastage_id, recordData);
        setSuccess(t('inventory.wastageLog.recordUpdated') || 'Wastage record updated successfully');
      } else {
        // Create new record
        await inventoryService.createWastageRecord(recordData);
        setSuccess(t('inventory.wastageLog.recordCreated') || 'Wastage record created successfully');
      }
      
      setFormOpen(false);
      setEditingRecord(null);
      fetchWastageRecords();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ WastageLog: Error saving record:', err);
      setError(err.message || 'Failed to save wastage record');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    
    try {
      setLoading(true);
      console.log('🗑️ WastageLog: Deleting record:', selectedRecord.wastage_id);
      
      await inventoryService.deleteWastageRecord(selectedRecord.wastage_id);
      
      setSuccess(t('inventory.wastageLog.recordDeleted') || 'Wastage record deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
      fetchWastageRecords();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ WastageLog: Error deleting record:', err);
      setError(err.message || 'Failed to delete wastage record');
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      console.log('📊 WastageLog: Exporting records...');
      
      const exportData = filteredRecords.map(record => ({
        Date: format(new Date(record.date), 'yyyy-MM-dd'),
        Product: record.product_name,
        'Quantity Wasted': record.quantity_wasted,
        Reason: wastageReasons.find(r => r.value === record.reason)?.label || record.reason,
        Notes: record.notes || '-',
        'Created By': record.created_by,
        'Created At': format(new Date(record.created_at), 'yyyy-MM-dd HH:mm')
      }));
      
      const csvData = convertToCSV(exportData);
      downloadCSV(csvData, `wastage-log-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      
      setSuccess(t('inventory.exportSuccess') || 'Export completed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ WastageLog: Export failed:', err);
      setError(t('inventory.exportError') || 'Export failed');
    }
  };

  // CSV utilities
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Get reason chip color and label
  const getReasonInfo = (reason) => {
    const reasonInfo = wastageReasons.find(r => r.value === reason);
    return reasonInfo || { label: reason, color: 'default' };
  };

  // Handle action menu
  const handleActionClick = (event, record) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedRecord(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setActionAnchorEl(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title={t('common.back') || 'Back'}>
                <IconButton 
                  onClick={() => navigate('/dashboard/inventory')}
                  sx={{ 
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                <TrendingDown sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {t('inventory.wastageLog.title') || 'Wastage Log'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('inventory.wastageLog.subtitle') || 'Track and manage product wastage records'}
                </Typography>
              </Box>
            </Box>
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchWastageRecords}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                {t('common.refresh') || 'Refresh'}
              </Button>
              {canExport && (
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={handleExport}
                  sx={{ borderRadius: 2 }}
                >
                  {t('common.export') || 'Export'}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddRecord}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ff5252 0%, #e53935 100%)',
                  }
                }}
              >
                {t('inventory.wastageLog.addRecord') || 'Add Record'}
              </Button>
            </Stack>
          </Box>
        </MotionDiv>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FilterList />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('inventory.filtersAndSearch') || 'Filters & Search'}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  sx={{ ml: 'auto' }}
                >
                  {filtersExpanded ? 'Less Filters' : 'More Filters'}
                </Button>
              </Box>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  placeholder={t('inventory.wastageLog.searchPlaceholder') || 'Search by product name or notes...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                
                {filtersExpanded && (
                  <>
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel>{t('inventory.product') || 'Product'}</InputLabel>
                      <Select
                        value={productFilter}
                        onChange={(e) => setProductFilter(e.target.value)}
                        label={t('inventory.product') || 'Product'}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="all">{t('inventory.allProducts') || 'All Products'}</MenuItem>
                        {products.map((product) => (
                          <MenuItem key={`product-${product.item_id}`} value={product.item_id}>
                            {product.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <FormControl sx={{ minWidth: 150 }}>
                      <InputLabel>{t('inventory.reason') || 'Reason'}</InputLabel>
                      <Select
                        value={reasonFilter}
                        onChange={(e) => setReasonFilter(e.target.value)}
                        label={t('inventory.reason') || 'Reason'}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="all">{t('inventory.allReasons') || 'All Reasons'}</MenuItem>
                        {wastageReasons.map((reason) => (
                          <MenuItem key={`reason-${reason.value}`} value={reason.value}>
                            {reason.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <DatePicker
                      label={t('inventory.dateFrom') || 'Date From'}
                      value={dateFrom}
                      onChange={setDateFrom}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                        }
                      }}
                    />
                    
                    <DatePicker
                      label={t('inventory.dateTo') || 'Date To'}
                      value={dateTo}
                      onChange={setDateTo}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                        }
                      }}
                    />
                  </>
                )}
              </Stack>
            </Box>
          </Card>
        </MotionDiv>

        {/* Main Content */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card sx={{ borderRadius: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inventory.date') || 'Date'}</TableCell>
                      <TableCell>{t('inventory.product') || 'Product'}</TableCell>
                      <TableCell align="right">{t('inventory.quantityWasted') || 'Quantity Wasted'}</TableCell>
                      <TableCell>{t('inventory.reason') || 'Reason'}</TableCell>
                      <TableCell>{t('inventory.notes') || 'Notes'}</TableCell>
                      <TableCell>{t('common.createdBy') || 'Created By'}</TableCell>
                      <TableCell align="center">{t('inventory.table.actions') || 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRecords.map((record, index) => {
                      const reasonInfo = getReasonInfo(record.reason);
                      
                      return (
                        <TableRow 
                          key={`wastage-${record.wastage_id}-${index}`}
                          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {format(new Date(record.date), 'MMM dd, yyyy')}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {record.product_name}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                              -{record.quantity_wasted}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Chip 
                              label={reasonInfo.label}
                              color={reasonInfo.color}
                              size="small"
                              icon={reasonInfo.color === 'error' ? <ErrorOutline /> : <Warning />}
                              sx={{ borderRadius: 1 }}
                            />
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {record.notes || '-'}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2">
                              {record.created_by}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(record.created_at), 'MMM dd, HH:mm')}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              {canEdit && (
                                <Tooltip title={t('common.edit') || 'Edit'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditRecord(record)}
                                    color="primary"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {(canEdit || canDelete) && (
                                <Tooltip title={t('common.moreActions') || 'More Actions'}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleActionClick(e, record)}
                                  >
                                    <MoreVert fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {filteredRecords.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm || productFilter !== 'all' || reasonFilter !== 'all'
                              ? (t('inventory.noRecordsFound') || 'No wastage records found matching your criteria')
                              : (t('inventory.wastageLog.noRecords') || 'No wastage records found')
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </MotionDiv>

        {/* Action Menu */}
        <Menu
          anchorEl={actionAnchorEl}
          open={Boolean(actionAnchorEl)}
          onClose={handleActionClose}
          PaperProps={{
            sx: { width: 160, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 2 }
          }}
        >
          {canEdit && (
            <MenuItem onClick={() => handleEditRecord(selectedRecord)}>
              <Edit fontSize="small" sx={{ mr: 1.5 }} />
              {t('common.edit') || 'Edit'}
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
              <Delete fontSize="small" sx={{ mr: 1.5 }} />
              {t('common.delete') || 'Delete'}
            </MenuItem>
          )}
        </Menu>

        {/* Wastage Form Dialog */}
        <WastageForm
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditingRecord(null);
          }}
          onSave={handleSaveRecord}
          initialData={editingRecord}
          products={products}
          loading={formLoading}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            color: 'white'
          }}>
            <Delete />
            {t('inventory.wastageLog.deleteRecord') || 'Delete Wastage Record'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography>
              {t('inventory.wastageLog.deleteConfirmation') || 'Are you sure you want to delete this wastage record? This action cannot be undone.'}
            </Typography>
            {selectedRecord && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'error.lighter' }}>
                <Typography variant="body2">
                  <strong>Product:</strong> {selectedRecord.product_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Quantity:</strong> {selectedRecord.quantity_wasted}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {format(new Date(selectedRecord.date), 'MMM dd, yyyy')}
                </Typography>
              </Paper>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2 }}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={handleDeleteRecord}
              variant="contained"
              color="error"
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? (t('common.deleting') || 'Deleting...') : (t('common.delete') || 'Delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default WastageLog;